import type { PurchaseResult } from '@/src/hooks/useRevenueCatPurchaseResult'
import type { Dispatch, RefObject } from 'react'
import type { PurchasesPackage } from 'react-native-purchases'
import { useCallback } from 'react'
import { useRouter } from 'expo-router'
import {
  buildPaywallDismissedProperties,
  MixpanelEvents,
  trackEvent,
} from '@/src/services/analytics'
import { showAlert } from '@/src/utils/alert'
import { addBreadcrumb } from '@/src/utils/sentry'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { PurchaseScreenAction } from '../utils/purchaseScreenState'

async function performLogout({
  dispatch,
  logout,
}: {
  dispatch: Dispatch<PurchaseScreenAction>
  logout: () => Promise<void>
}) {
  dispatch({ type: 'setLoggingOut', value: true })
  try {
    await logout()
  } catch (error) {
    console.error('Logout failed', error)
    dispatch({ type: 'setLoggingOut', value: false })
  }
}

export function usePurchaseFlow({
  dispatch,
  isLoggingOut,
  logout,
  openTimeRef,
  purchase,
  selectedPackage,
}: {
  dispatch: Dispatch<PurchaseScreenAction>
  isLoggingOut: boolean
  logout: () => Promise<void>
  openTimeRef: RefObject<number | null>
  purchase: (pkg: PurchasesPackage) => Promise<PurchaseResult>
  selectedPackage: PurchasesPackage | null
}) {
  const { t } = useTranslation(['common', 'purchase', 'profile', 'setup'])
  const router = useRouter()

  const navigateAfterAccessGranted = useCallback(async () => {
    router.replace('/auth-complete')
    return
  }, [router])

  const purchaseMutation = useMutation({
    mutationFn: async (pkg: NonNullable<typeof selectedPackage>) => {
      const result = await purchase(pkg)

      switch (result.outcome) {
        case 'success':
          await navigateAfterAccessGranted()
          break
        case 'cancelled':
          dispatch({ type: 'clearFeedback' })
          break
        case 'ownership_mismatch':
          dispatch({ type: 'clearFeedback' })
          addBreadcrumb('purchase', 'paywall_ownership_mismatch_alert_shown', {
            currentRevenueCatId: result.currentRevenueCatId ?? 'missing',
            originalAppUserId: result.originalAppUserId,
          })
          showAlert(
            t('profile:restore_purchases_mismatch_title'),
            t('profile:restore_purchases_mismatch_message'),
          )
          break
        case 'requires_restore':
          dispatch({ type: 'clearFeedback' })
          addBreadcrumb('purchase', 'paywall_requires_restore_alert_shown')
          showAlert(
            t('profile:restore_purchases_mismatch_title'),
            t('profile:restore_purchases_mismatch_message'),
          )
          break
        case 'error': {
          dispatch({ type: 'clearFeedback' })
          const message = result.message || t('purchase:purchase_failed')
          showAlert(t('common:error'), message)
          break
        }
      }
    },
  })

  const handlePurchase = useCallback(async () => {
    if (!selectedPackage || purchaseMutation.isPending) {
      return
    }

    dispatch({ type: 'clearFeedback' })
    await purchaseMutation.mutateAsync(selectedPackage)
  }, [dispatch, purchaseMutation, selectedPackage])

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) {
      return
    }

    trackEvent(
      MixpanelEvents.PaywallDismissed,
      buildPaywallDismissedProperties({
        timeOnScreenS: Math.round(
          (Date.now() - (openTimeRef.current ?? Date.now())) / 1000,
        ),
        planShown: selectedPackage?.identifier,
      }),
    )
    await performLogout({ dispatch, logout })
  }, [dispatch, isLoggingOut, logout, openTimeRef, selectedPackage?.identifier])

  return {
    handleLogout,
    handlePurchase,
    isProcessing: purchaseMutation.isPending,
    purchaseMutation,
  }
}
